import { memo, useContext } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, FormattedPlural } from "react-intl";
import Modal from "react-modal";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import "./QuotationRoomDetailsModal.scss";
import RoomCapacity from "app/pages/.shared/RoomCapacity.jsx";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import AppGlobalsContext from "app/AppGlobalsContext";
import { ImagesCarousel } from "app/pages/SmartDP/Listing/SmartDPProduct/ImagesCarousel";
import { htmlSanitize } from "app/utils/sanitize";
import { CrossIcon } from "app/icons/navigation";

const IMG_SIZES_DESKTOP = {
	large: { width: 1272, height: 734 },
	xlarge: { width: 1272, height: 734 },
};

const QuotationRoomDetailsModal = ({ isOpen = false, onClose = () => {}, rooms = [] }) => {
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	return (
		<Modal
			isOpen={isOpen}
			closeTimeoutMS={200}
			onRequestClose={onClose}
			overlayClassName="quotation-room-details-modal__overlay"
			className="quotation-room-details-modal__content"
		>
			<header className="quotation-room-details-modal__header">
				<Typography
					component="h2"
					isBold
					variant={TYPOGRAPHY_VARIANTS.XL2}
					className="quotation-room-details-modal__title"
				>
					<FormattedPlural
						value={rooms?.length}
						one={<FormattedMessage id="room.details.singular" />}
						other={<FormattedMessage id="room.details.plural" />}
					/>
				</Typography>

				<Button
					variant={BUTTON_VARIANTS.BORDERLESS}
					size={BUTTON_SIZES.LARGE}
					className="quotation-room-details-modal__close-button"
					onClick={onClose}
					icon={<CrossIcon />}
				/>
			</header>

			<main className="quotation-room-details-modal__rooms-list">
				{rooms.map(room => {
					const hasPhotos = room?.photos?.length > 0;
					const hasAmenities = room?.amenities?.length > 0;
					const hasDescription = room?.description?.length > 0;

					return (
						<article key={room.id} className="quotation-room-details-modal__room">
							<Typography
								className="quotation-room-details-modal__room-title"
								variant={TYPOGRAPHY_VARIANTS.LARGE}
								component="h3"
								isBold
							>
								{room.title}{" "}
								<Typography
									className="quotation-room-details-modal__room-details"
									variant={TYPOGRAPHY_VARIANTS.LARGE}
									isBold
									component="span"
								>
									{room?.area?.value > 0 &&
										` [${room.area.value} ${room?.area?.unit}]`}
									{Object.keys(room?.occupancy)?.length > 0 && (
										<RoomCapacity
											adultCount={room.occupancy?.adults}
											babyCount={room.occupancy?.babies}
											childCount={room.occupancy?.children}
										/>
									)}
								</Typography>
							</Typography>

							{hasPhotos && !isMobile && (
								<div className="quotation-room-details-modal__room-carousel">
									<ImagesCarousel
										images={room.photos}
										alt={room.title}
										imgSizesDesktop={IMG_SIZES_DESKTOP}
										showImageIndex
									/>
								</div>
							)}

							{hasAmenities && (
								<ul className="quotation-room-details-modal__room-amenities">
									{room.amenities.map(amenity => {
										return (
											<li
												key={amenity}
												className="quotation-room-details-modal__room-amenities-item"
											>
												<Typography
													variant={TYPOGRAPHY_VARIANTS.REGULAR}
													component="span"
												>
													{amenity}
												</Typography>
											</li>
										);
									})}
								</ul>
							)}

							{hasDescription && (
								<div
									dangerouslySetInnerHTML={{
										__html: htmlSanitize(room.description),
									}}
								/>
							)}

							{!hasPhotos && !hasAmenities && !hasDescription && (
								<Typography
									variant={TYPOGRAPHY_VARIANTS.REGULAR}
									component="p"
									className="quotation-room-details-modal__content-unavailable"
								>
									<FormattedMessage id="room.content.unavailable" />
								</Typography>
							)}
						</article>
					);
				})}
			</main>
		</Modal>
	);
};

QuotationRoomDetailsModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	rooms: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			title: PropTypes.string.isRequired,
			photos: PropTypes.arrayOf(PropTypes.string).isRequired,
			amenities: PropTypes.arrayOf(PropTypes.string).isRequired,
			occupancy: PropTypes.shape({
				adults: PropTypes.number.isRequired,
				children: PropTypes.number.isRequired,
				babies: PropTypes.number.isRequired,
			}),
		})
	).isRequired,
};

export default memo(QuotationRoomDetailsModal);
